import { format, isToday, isSameMonth, isSameDay, parseISO } from 'date-fns';
import { nl } from 'date-fns/locale';
import { cn } from '@/lib/utils';
import { CalendarEvent } from '@/hooks/useOutlookCalendar';

interface CalendarDayCellProps {
  day: Date;
  currentMonth: Date;
  events: CalendarEvent[];
  isSelected: boolean;
  onSelect: (day: Date) => void;
}

export function CalendarDayCell({ 
  day, 
  currentMonth, 
  events, 
  isSelected,
  onSelect 
}: CalendarDayCellProps) {
  const dayEvents = events.filter(event => {
    if (!event.start_time) return false;
    try {
      const eventDate = parseISO(event.start_time);
      return isSameDay(eventDate, day);
    } catch {
      return false;
    }
  });

  const isCurrentMonth = isSameMonth(day, currentMonth);
  const isCurrentDay = isToday(day);

  return (
    <button
      onClick={() => onSelect(day)}
      className={cn(
        "relative h-24 md:h-28 p-2 text-left transition-all duration-200 border-r border-b border-border/50",
        "hover:bg-accent/50 focus:outline-none focus:ring-2 focus:ring-energy-green/50 focus:ring-inset",
        !isCurrentMonth && "bg-muted/30 text-muted-foreground",
        isSelected && "bg-energy-green/10 ring-2 ring-energy-green ring-inset",
        isCurrentDay && !isSelected && "bg-energy-blue/5"
      )}
    >
      {/* Day number */}
      <div className={cn(
        "inline-flex items-center justify-center w-7 h-7 rounded-full text-sm font-medium",
        isCurrentDay && "bg-energy-green text-white",
        !isCurrentDay && isCurrentMonth && "text-foreground",
        !isCurrentDay && !isCurrentMonth && "text-muted-foreground"
      )}>
        {format(day, 'd')}
      </div>

      {/* Events preview */}
      <div className="mt-1 space-y-1 overflow-hidden">
        {dayEvents.slice(0, 3).map((event, idx) => (
          <div 
            key={event.id}
            className={cn(
              "text-xs px-1.5 py-0.5 rounded truncate",
              idx === 0 && "bg-energy-green/20 text-energy-green",
              idx === 1 && "bg-energy-blue/20 text-energy-blue",
              idx === 2 && "bg-energy-orange/20 text-energy-orange"
            )}
          >
            {event.subject}
          </div>
        ))}
        {dayEvents.length > 3 && (
          <div className="text-xs text-muted-foreground px-1.5">
            +{dayEvents.length - 3} meer
          </div>
        )}
      </div>

      {/* Event count badge for mobile */}
      {dayEvents.length > 0 && (
        <div className="absolute bottom-2 right-2 md:hidden">
          <div className="w-5 h-5 rounded-full bg-energy-green text-white text-xs 
            flex items-center justify-center font-medium">
            {dayEvents.length}
          </div>
        </div>
      )}
    </button>
  );
}
